// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import useFetch from "@/hooks/useFetch";
import useLang from "@/hooks/useLang";
import useTranslation from "@/hooks/useTranslation";
import type { TySubscription } from "@/types";
import { formatMoney, readLangValue } from "@/utils";
import Icon from "@/UI/atoms/Icon";
import StepperInput from "@/UI/inputs/StepperInput";
// --- Types --------------------------------------------------------------------------------------
export type TyAddStorageDetails = { subscription: TySubscription; onDone: () => void };

type Props = { onClose: () => void; details?: unknown };
// --- Logic --------------------------------------------------------------------------------------
/**
 * Buys extra storage on a live period — the **one** additive change a paid row ever takes, because
 * storage is bought the day a company runs out and not the day its period renews.
 *
 * It does not touch the period's recorded amount: that figure is what was charged for the period and
 * has to keep matching its receipt. The addon was its own transfer.
 */
export default function AddStoragePopup({ onClose, details }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";
    const { subscription, onDone } = (details ?? {}) as TyAddStorageDetails;
    const name = readLangValue(subscription.organization.name, lang) || subscription.organization.slug;
    const [count, setCount] = useState(1);

    const save = useFetch(`/subscriptions/${subscription._id}/storage`, { method: "POST" }, false);

    useEffect(() => {
        if (!save.succeededAt) return;
        onDone?.();
        onClose();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [save.succeededAt]);

    const chosen = Math.max(1, count);
    const unitPrice = constants.storageAddon[subscription.period];
    const total = chosen * unitPrice;

    return (
        <section className="popup-card add-storage">
            <header className="add-storage__head">
                <h2 className="add-storage__title">{t.subscriptions.storageTitle.replace("{name}", name)}</h2>
                <button type="button" className="add-storage__close" onClick={onClose} aria-label={t.common.close}>
                    <Icon name="close" size={16} />
                </button>
            </header>

            {/* Stepped rather than picked from a list: there is no rule about how many packs a
                company may be sold, and the list said there were eight. */}
            <StepperInput
                label={t.subscriptions.storageCount}
                hint={`${chosen} × ${constants.storageAddon.gb} GB`}
                value={count}
                onChange={setCount}
                min={1}
                disabled={save.loading}
            />

            {/* What it costs, worked out here so nobody has to multiply it themselves. */}
            <p className="add-storage__total">
                {formatMoney(total)} {t.subscriptions.currency}
            </p>

            <p className="add-storage__hint">{t.subscriptions.storageHint}</p>
            {save.error && <p className="add-storage__error">{save.error}</p>}

            <div className="add-storage__ctas">
                <button type="button" className="add-storage__cancel" onClick={onClose} disabled={save.loading}>
                    {t.common.cancel}
                </button>
                <button
                    type="button"
                    className="add-storage__submit"
                    onClick={() => save.invoke({ method: "POST", data: { count: chosen } })}
                    disabled={save.loading}
                >
                    {save.loading ? t.common.loading : t.subscriptions.addStorage}
                </button>
            </div>
        </section>
    );
}
